import { Theme, type CSSObject } from "@emotion/react";
import styled from "@emotion/styled";
import { PortableText, PortableTextProps } from "@portabletext/react";
import type { CSSProperties } from "react";
import React, { PropsWithChildren } from "react";
import YouTubePlayer from "react-player/youtube";

import {
  ArticleTable,
  Cell,
  Row,
} from "@brexhq/next/src/components/shared/ArticleTable/ArticleTable";
import {
  H2,
  H3,
  H4,
  H5,
  H6,
  Paragraph,
  TableText,
} from "@brexhq/next/src/components/shared/HelpCenter/Typography";
import { Link } from "@brexhq/next/src/components/shared/Link/Link";
import breakpoints from "@brexhq/shared/styles/breakpoints";
import { defineResponsiveSpacingVariable } from "@brexhq/shared/styles/spacing";
import { CellType } from "@brexhq/shared/types";
import { generateUrlFromReference } from "@brexhq/shared/utils/generateUrlFromReference";
import { blocksToText } from "@brexhq/shared/utils/sanity/blocksToText";
import { slugify } from "@brexhq/shared/utils/slugify";

import CopyLibraryItem from "../../shared/BlockRenderer/CopyLibraryItem";
import { BynderAsset } from "../../shared/BynderAsset/BynderAsset";
import { Accordion } from "../../shared/HelpCenter/Accordion";

type Props = Pick<PortableTextProps, "value"> & {
  normalTextComponent?: React.FunctionComponent<PropsWithChildren>;
};

const DEFAULT_MEDIA_CONFIG = {
  imageAlignment: "start",
  imageMaxWidth: 1500,
};

const InlineSpan = styled.span(({ theme }) => ({
  display: "inline-flex",
  verticalAlign: "middle",
  fontVariationSettings: theme.rebirth.fonts.FontWeight.MEDIUM,
}));

const getAccentColorMap = (theme: Theme) => ({
  blue: theme.colors.accent.blue.content.default,
  red: theme.colors.negative.default,
  gray: theme.colors.content.secondary,
  green: theme.colors.positive.default,
  yellow: theme.colors.warning.default,
  purple: theme.colors.accent.purple.content.default,
  orange: theme.colors.accent.primary.background.pressed,
});

const getSecondaryColorMap = (theme: Theme) => ({
  blue: theme.colors.accent.blue.background.default,
  red: theme.colors.negative.muted,
  gray: theme.colors.content.inverse.secondary,
  green: theme.colors.positive.muted,
  yellow: theme.colors.warning.muted,
  purple: theme.colors.accent.purple.background.pressed,
  orange: theme.colors.accent.yellow.background.default,
});

const statusVariantStylesMap: Record<
  "body" | "accordion",
  (_: { theme: Theme }) => CSSObject
> = {
  body: ({ theme }: { theme: Theme }) => ({
    padding: "3.6px 12px",
    fontSize: "21.6px",
    lineHeight: "28.8px",
    letterSpacing: "-0.432px",
    [breakpoints.SML]: {
      marginLeft: 0,
      padding: "2px 8px",
      ...theme.typography.DetailLargeM,
    },
  }),
  accordion: () => ({
    padding: "2.4px 7.2px",
    fontSize: "14.4px",
    lineHeight: "19.2px",
    letterSpacing: "-0.288px",
    ":first-of-type": {
      marginLeft: "8px",
    },
    [breakpoints.SML]: {
      padding: "1.6px 4.8px",
      fontSize: "9.6px",
      lineHeight: "12.8px",
      letterSpacing: "-0.192px",
    },
  }),
};

const StyledStatus = styled(InlineSpan, {
  shouldForwardProp: (prop) =>
    prop !== "emphasis" && prop !== "color" && prop !== "variant",
})<{
  color: string;
  emphasis: boolean;
  variant: keyof typeof statusVariantStylesMap;
}>(({ emphasis, color, theme, variant }) => {
  const accentColorMap = getAccentColorMap(theme);
  const accentColor = accentColorMap[color] || accentColorMap.blue;
  const secondaryColorMap = getSecondaryColorMap(theme);
  const secondaryColor = secondaryColorMap[color] || secondaryColorMap.blue;

  const variantStyles = (
    statusVariantStylesMap[variant] || statusVariantStylesMap.body
  )({ theme });

  return {
    display: "inline-flex",
    placeItems: "center",
    placeContent: "center",
    width: "fit-content",
    minHeight: "20px",
    borderRadius: "9999px",
    backgroundColor: emphasis ? accentColor : secondaryColor,
    color: emphasis ? secondaryColor : accentColor,
    fontWeight: "500",
    ...variantStyles,
  };
});

export const TagReference = ({
  color,
  shade,
  text,
  variant,
}: {
  text: React.ReactNode;
  shade: string;
  color: string;
  variant: keyof typeof statusVariantStylesMap;
}) => (
  <StyledStatus color={color} emphasis={shade === "dark"} variant={variant}>
    {text}
  </StyledStatus>
);

const WithContentMargin = styled("div", {
  shouldForwardProp: (propName) =>
    propName !== "disableMargin" && propName !== "as",
})<{ disableMargin?: boolean }>`
  --margin: ${({ disableMargin }) => (disableMargin ? 0 : 32)}px;
  ${breakpoints.SML} {
    --margin: ${({ disableMargin }) => (disableMargin ? 0 : 16)}px;
  }

  &:not(:first-child) {
    margin-top: var(--margin);
  }

  &:not(:last-child) {
    margin-bottom: var(--margin);
  }
`;

const Normal = styled(WithContentMargin)`
  white-space: pre-line;
`.withComponent(Paragraph);

const Divider = styled.hr`
  ${defineResponsiveSpacingVariable("--v-margin", "xlrg")};
  margin-block: var(--v-margin);
  ${({ theme }) => theme.borders.defaultBorder.top}

  details + & {
    border-top: none;
    margin-top: 0;
  }
`;

type HeadingLevel = "h2" | "h3" | "h4" | "h5" | "h6";

const headingLevelToComponentMap = {
  h2: H2,
  h3: H3,
  h4: H4,
  h5: H5,
  h6: H6,
};

type alignmentType = "flex-start" | "flex-end" | "center";

const StyledYoutubeVideo = styled.div<{
  alignment: alignmentType;
}>(({ alignment }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: alignment,
}));

const Heading = ({
  level,
  children,
  value,
}: {
  level: HeadingLevel;
  children: React.ReactNode;
  value: unknown;
}) => {
  const Component = React.useMemo(
    () => WithContentMargin.withComponent(headingLevelToComponentMap[level]),
    [level],
  );

  const id = React.useMemo(() => slugify(blocksToText([value])), [value]);

  return <Component id={id}>{children}</Component>;
};

const getAlignment = (alignment: string) => {
  switch (alignment) {
    case "left":
      return "flex-start";
    case "right":
      return "flex-end";
    case "center":
    default:
      return "center";
  }
};

const ImageExternalWrapper = styled(WithContentMargin)<{
  align?: CSSProperties["justifyContent"];
}>`
  display: flex;
  place-content: ${({ align }) => align || "start"};
  place-items: ${({ align }) => align || "start"};

  ${breakpoints.SML} {
    place-content: stretch;
    place-items: stretch;
  }
`;
const ImageInternalWrapper = styled.div`
  padding: 16px;
  ${({ theme }) => theme.borders.defaultBorder.all}
  border-radius: ${({ theme }) => theme.borders.radius.small}px;

  ${breakpoints.SML} {
    width: 100%;
    display: flex;
    place-content: center;
    place-items: center;
  }
`;

export default function HelpCenterBlockContent({
  value,
  normalTextComponent: NormalTextComponent = Normal,
}: Props) {
  return (
    <PortableText
      value={value}
      components={{
        hardBreak: () => <>{"\n"}</>,
        marks: {
          link: ({ children, value }) => {
            return (
              <Link
                component="helpCenterArticle"
                section="table"
                to={generateUrlFromReference({
                  internalSiteUrlReference: value?.internalSiteUrlReference,
                  queryParamString: value?.queryParamString,
                  link: value?.href,
                  hash: value?.hash,
                })}
                label={children?.[0]?.toString() || ""}
              >
                {children}
              </Link>
            );
          },
        },
        block: {
          h2: ({ children, value, index }) => (
            <>
              {index > 0 && <Divider />}
              <Heading level="h2" value={value}>
                {children}
              </Heading>
            </>
          ),
          h3: ({ children, value }) => (
            <Heading level="h3" value={value}>
              {children}
            </Heading>
          ),
          h4: ({ children, value }) => (
            <Heading level="h4" value={value}>
              {children}
            </Heading>
          ),
          h5: ({ children, value }) => (
            <Heading level="h5" value={value}>
              {children}
            </Heading>
          ),
          h6: ({ children, value }) => (
            <Heading level="h6" value={value}>
              {children}
            </Heading>
          ),
          normal: ({ children }) => (
            <NormalTextComponent>{children}</NormalTextComponent>
          ),
        },
        list: {
          bullet: ({ children }) => <Normal as="ul">{children}</Normal>,
          number: ({ children }) => <Normal as="ol">{children}</Normal>,
        },
        listItem: ({ children }) => (
          <Normal disableMargin as="li">
            {children}
          </Normal>
        ),
        types: {
          helpCenterMedia({ value }) {
            /**
             * The initial root of the issue was that all new assets are using the new media type, which has
             * 2 nested children objects. Previously the (old) media type had 1 nested child; recently there
             * was a migration to update all the asset/image metadata to include the ability to add a border
             * -radius configuration for sanity users when creating the adding the new asset to the article/
             * page (and thus, adding the extra child object).
             *
             * From my understanding, it looks like help center images were missed during this migration so
             * the BynderMediaPreview component doesn't work with the old media type.
             *
             * By switching it to just use the default bynder.asset type that doesn't require any additional
             * configuration (such as border radius), we have to be able to handle this type as well as the
             * new "media" type within the BynderMediaPreview component and the actual display of the article
             * in HelpCenterBlockContent.
             *
             * Moving forward we'd probably want to migrate the images just so everything is the same.
             *
             **/
            const media = value.asset?.media?.type
              ? value.asset.media
              : value.asset;

            const {
              imageAlignment = DEFAULT_MEDIA_CONFIG.imageAlignment,
              imageMaxWidth = DEFAULT_MEDIA_CONFIG.imageMaxWidth,
            } = value;

            return (
              <ImageExternalWrapper align={imageAlignment}>
                <ImageInternalWrapper>
                  <BynderAsset
                    {...value}
                    loopVideo={!value.disableVideoLoop}
                    showVideoControls={value.showVideoControls ?? true}
                    asset={media}
                    maxWidth={imageMaxWidth}
                  />
                </ImageInternalWrapper>
              </ImageExternalWrapper>
            );
          },
          articleTable({ value }) {
            return (
              <ArticleTable>
                {value.rows.map((row: any, index: number, rows: any[]) => {
                  return row.cells ? (
                    <Row rowCount={rows.length} rowIndex={index} key={row._key}>
                      {row.cells.map(
                        (column: any, index: number, columns: any[]) => {
                          const cellProps = {
                            columnCount: columns.length,
                            columnIndex: index,
                          };
                          switch (column.type as CellType) {
                            case "data":
                              return (
                                <Cell
                                  as="td"
                                  key={column._key}
                                  {...cellProps}
                                  verticalAlign="middle"
                                >
                                  <HelpCenterBlockContent
                                    value={column.content}
                                    normalTextComponent={TableText}
                                  />
                                </Cell>
                              );

                            case "rowHeader":
                              return (
                                <Cell
                                  as="th"
                                  scope="row"
                                  key={column._key}
                                  {...cellProps}
                                  verticalAlign="middle"
                                >
                                  <TableText>
                                    <strong>{column.content}</strong>
                                  </TableText>
                                </Cell>
                              );

                            case "columnHeader":
                              return (
                                <Cell
                                  as="th"
                                  scope="col"
                                  {...cellProps}
                                  verticalAlign="bottom"
                                >
                                  <TableText>
                                    <strong>{column.content}</strong>
                                  </TableText>
                                </Cell>
                              );
                          }
                        },
                      )}
                    </Row>
                  ) : null;
                })}
              </ArticleTable>
            );
          },
          copyLibraryReference: ({ value }) => {
            return <CopyLibraryItem value={value} />;
          },
          tagReference: ({ value }) => (
            <TagReference variant="body" {...value.tagItem} />
          ),
          helpCenterAccordion: ({ value: { header, content, tags } }) => (
            <Accordion
              header={
                <>
                  {header}
                  {tags?.map((tag) => (
                    <React.Fragment key={tag._key}>
                      {" "}
                      <TagReference variant="accordion" {...tag.tagItem} />
                    </React.Fragment>
                  ))}
                </>
              }
            >
              <HelpCenterBlockContent value={content} />
            </Accordion>
          ),
          youtube: ({ value }) => {
            const alignment = getAlignment(value.alignment);
            return (
              <>
                {typeof value.url === "string" ? (
                  <StyledYoutubeVideo alignment={alignment}>
                    <YouTubePlayer
                      url={value.url}
                      loop={value.loop}
                      muted={value.autoplay}
                      playing={value.autoplay}
                      controls={value.controls}
                      height={value.height}
                      width={value.width}
                    />
                  </StyledYoutubeVideo>
                ) : null}
              </>
            );
          },
        },
      }}
    />
  );
}
